import { useEffect, useRef, useState, useCallback } from 'react';
import karateChopSprite from '../Moves/KarateChop.png';
import './KarateChopAnimation.css';

/**
 * OPTIMIZED KARATE CHOP ANIMATION
 * Based on Pokémon Emerald reference analysis
 * 
 * Key Insights:
 * - Uses frameIndex 0 (black hand silhouette) and frameIndex 1 (follow-up)
 * - Direct impact effect, not projectile animation
 * - Instant appearance, short duration, smooth fade
 * - Centered perfectly over target
 */
export default function KarateChopAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');
  
  // Animation phase transition hook
  const transitionTo = useCallback((phase, delay = 0) => {
    setTimeout(() => setAnimationPhase(phase), delay);
  }, []);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent (target)
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // SPRITE CONFIGURATION - KarateChop.png (384x192, 2 frames of 192x192 each)
    const SPRITE_CONFIG = {
      frameWidth: 192,   // Width of each frame (192x192)
      frameHeight: 192,  // Height of sprite
      impactFrame: 0,    // frameIndex=0 (black hand silhouette)
      peakFrame: 1,      // frameIndex=1 (follow-up effect)
    };

    // Calculate perfect center positioning over target
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Enhanced for sliding motion and visibility
    const TIMING = {
      preparation: 120,    // Player stance preparation
      slideIn: 100,        // Hand sliding into position
      handImpact: 200,     // frameIndex=0 duration (hand silhouette with slide)
      handPeak: 250,       // frameIndex=1 duration (extended for visibility)
      fadeOut: 180,        // Clean fade out
      total: 850,          // Total animation duration
    };

    console.log('🥋 Karate Chop Animation - Optimized Version Starting');
    
    // PHASE 1: PREPARATION (0-120ms)
    // Player fighting stance preparation
    setAnimationPhase('preparation');
    playerEl.style.transition = 'transform 120ms ease-in-out';
    playerEl.style.transform = 'scale(1.05) rotate(-2deg)';
    
    // Subtle target highlight preparation
    opponentEl.style.transition = 'filter 120ms ease-in';
    opponentEl.style.filter = 'brightness(1.1)';
    
    // PHASE 2: SLIDING HAND IMPACT - frameIndex=0 with motion (120-420ms)
    const handImpactTimer = setTimeout(() => {
      setAnimationPhase('hand_impact');
      console.log('🥋 Phase 2: Sliding Hand Impact (frameIndex=0)');
      
      // Create hand silhouette effect - frameIndex=0
      const handEffect = document.createElement('div');
      handEffect.className = 'karate-chop-hand-impact';
      
      // Start position: dramatic slide from left to right for clear visibility
      const startX = centerX - SPRITE_CONFIG.frameWidth / 2 - 80; // Start 80px left
      const endX = centerX - SPRITE_CONFIG.frameWidth / 2 + 40;   // End 40px right
      const yPos = centerY - SPRITE_CONFIG.frameHeight / 2;
      
      handEffect.style.left = `${startX}px`;
      handEffect.style.top = `${yPos}px`;
      handEffect.style.width = `${SPRITE_CONFIG.frameWidth}px`;
      handEffect.style.height = `${SPRITE_CONFIG.frameHeight}px`;
      
      // Set to frameIndex=0 (black hand silhouette) - Fixed dimensions
      handEffect.style.backgroundImage = `url(${karateChopSprite})`;
      handEffect.style.backgroundSize = '384px 192px'; // Full sprite sheet
      handEffect.style.backgroundPosition = '0px 0px'; // frameIndex=0 (first 192px)
      handEffect.style.backgroundRepeat = 'no-repeat';
      handEffect.style.position = 'absolute';
      handEffect.style.zIndex = '90';
      handEffect.style.pointerEvents = 'none';
      handEffect.style.transition = 'left 150ms ease-out'; // Slower for visibility
      
      container.appendChild(handEffect);
      animRef.current = handEffect;
      
      // Animate dramatic sliding motion across target
      setTimeout(() => {
        if (animRef.current) {
          animRef.current.style.left = `${endX}px`;
          console.log(`🥋 Sliding from ${startX}px to ${endX}px (${endX - startX}px travel)`);
        }
      }, 30); // Slight delay for dramatic slide start
      
      // Flash effect at impact center
      setTimeout(() => {
        const flashEffect = document.createElement('div');
        flashEffect.className = 'karate-chop-flash';
        flashEffect.style.left = `${centerX}px`;
        flashEffect.style.top = `${centerY}px`;
        container.appendChild(flashEffect);
        
        // Clean up flash after animation
        setTimeout(() => {
          if (flashEffect.parentNode) {
            flashEffect.parentNode.removeChild(flashEffect);
          }
        }, 200);
      }, TIMING.slideIn + 20); // Slight delay after slide completes
      
    }, TIMING.preparation);
    
    // PHASE 3: HAND PEAK - frameIndex=1 (420-670ms) - Extended visibility
    const handPeakTimer = setTimeout(() => {
      setAnimationPhase('hand_peak');
      console.log('🥋 Phase 3: Hand Peak (frameIndex=1) - Extended Duration');
      
      if (animRef.current) {
        // Transition to frameIndex=1 with correct positioning
        animRef.current.style.backgroundPosition = '-192px 0px'; // frameIndex=1 (second 192px frame)
        animRef.current.className = 'karate-chop-hand-peak';
        animRef.current.style.transition = 'none'; // Remove slide transition
        
        // Center the after-effect for better visibility
        const centerXPos = centerX - SPRITE_CONFIG.frameWidth / 2;
        animRef.current.style.left = `${centerXPos}px`;
        
        console.log('🥋 Switched to frameIndex=1 (after-effect) at position -192px');
        
        // Opponent hit reaction
        opponentEl.classList.add('karate-chop-hit');
      }
      
    }, TIMING.preparation + TIMING.slideIn + TIMING.handImpact);
    
    // PHASE 4: FADE OUT (670-850ms)
    const fadeOutTimer = setTimeout(() => {
      setAnimationPhase('fade_out');
      console.log('🥋 Phase 4: Fade Out');
      
      if (animRef.current) {
        animRef.current.className = 'karate-chop-fade-out';
      }
      
      // Reset opponent
      opponentEl.style.filter = '';
      opponentEl.classList.remove('karate-chop-hit');
      
    }, TIMING.preparation + TIMING.slideIn + TIMING.handImpact + TIMING.handPeak);
    
    // PHASE 5: COMPLETE (850ms)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      console.log('🥋 Phase 5: Animation Complete');
      
      // Reset player position
      playerEl.style.transition = 'transform 200ms ease-out';
      playerEl.style.transform = '';
      
      // Clean up hand effect
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Animation complete callback
      setTimeout(() => {
        onComplete();
      }, 100);
      
    }, TIMING.total);

    // Enhanced cleanup function
    return () => {
      clearTimeout(handImpactTimer);
      clearTimeout(handPeakTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      if (opponentEl) {
        opponentEl.style.transition = '';
        opponentEl.style.filter = '';
        opponentEl.classList.remove('karate-chop-hit');
      }
      
      console.log('🥋 Karate Chop Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null;
}

// Set the move type for proper coloring in the battle UI
KarateChopAnimation.moveType = "fighting";
